/**
 * Core-100 R2 — CORE-BQ: the independent server check. The browser audits on this page are
 * authored by a client (what it looked at, what it found); this card shows the run the SERVER
 * recorded by reading the data itself (`run_server_integrity_scan`, daily). No run is never shown
 * as healthy.
 *
 * Core-100 R4 — one interpretation. The card used to read only `verdict` and decide for itself what
 * it meant, so a week-old server PASS still said "found nothing" and a run that did not finish looking
 * was described from its verdict alone. It now reads the whole run and interprets it through
 * `deriveIntegrityState` — the same derivation the browser-audit headline uses — so coverage and
 * staleness demote a server run exactly as they demote a browser run. The query is exported
 * (`fetchLatestServerIntegrityRun`) so the real-stack proof reads the server's run through the very
 * code this card runs.
 */
import { useQuery } from '@tanstack/react-query';
import type { SupabaseClient } from '@supabase/supabase-js';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';
import { deriveIntegrityState, type IntegrityRunRow } from '@/utils/operationalIntegrity';

export interface ServerFinding { id: string; rule: string; severity: string; message: string; record_id: string | null }
export interface ServerIntegrityRead { run: IntegrityRunRow | null; findings: ServerFinding[] }

const RUN_COLUMNS = 'id, verdict, coverage_complete, violation_count, rules_checked, rules_unevaluated, checked_at, entry_point, source';

/** The newest run the SERVER recorded for this shop, and its findings. Throws on a failed read. */
export async function fetchLatestServerIntegrityRun(client: SupabaseClient, shopId: string): Promise<ServerIntegrityRead> {
  const { data, error } = await client
    .from('integrity_audit_runs')
    .select(RUN_COLUMNS)
    .eq('shop_id', shopId)
    .eq('entry_point', 'server_scan')
    .order('checked_at', { ascending: false })
    .limit(1);
  if (error) throw error;
  const run = ((data ?? [])[0] ?? null) as IntegrityRunRow | null;
  if (!run || run.violation_count === 0) return { run, findings: [] };
  const { data: v, error: vErr } = await client
    .from('integrity_violations')
    .select('id, rule, severity, message, record_id')
    .eq('run_id', run.id);
  if (vErr) throw vErr;
  return { run, findings: (v ?? []) as ServerFinding[] };
}

/** One sentence about the server's run, derived through the shared interpretation. */
export function describeServerRun(run: IntegrityRunRow | null, now: Date = new Date()): string {
  const status = deriveIntegrityState(run, now);
  switch (status.state) {
    case 'NEVER_AUDITED':
      return 'The server has not checked this shop yet, so it says nothing either way.';
    case 'VIOLATIONS_FOUND':
      return `The server found ${run!.violation_count} contradiction${run!.violation_count === 1 ? '' : 's'}.`;
    case 'AUDIT_INCOMPLETE':
      return 'The server check did not finish; it says nothing about the board.';
    case 'STALE':
      return 'The server\'s last check found nothing, but it is too old to describe the board as it stands now.';
    case 'HEALTHY':
      return `The server found nothing contradictory in this shop's live work (${run!.rules_checked} rule${run!.rules_checked === 1 ? '' : 's'} checked).`;
  }
}

export function ServerIntegrityCard({ shopId }: { shopId: string }) {
  const runQuery = useQuery({
    queryKey: ['integrity-server-run', shopId],
    queryFn: () => fetchLatestServerIntegrityRun(supabase, shopId),
  });
  return (
    <Card data-testid="server-integrity">
      <CardHeader>
        <CardTitle className="text-base">Independent server check</CardTitle>
        <CardDescription data-testid="server-integrity-headline">
          {runQuery.isError
            ? 'The server check could not be read — this is not a statement about the board.'
            : runQuery.isLoading ? 'Loading…' : describeServerRun(runQuery.data?.run ?? null)}
        </CardDescription>
      </CardHeader>
      {(runQuery.data?.findings.length ?? 0) > 0 && (
        <CardContent>
          <ul className="space-y-2">
            {runQuery.data!.findings.map((f) => (
              <li key={f.id} className="text-sm">
                <Badge variant={f.severity === 'error' ? 'destructive' : 'secondary'} className="mr-2">{f.severity}</Badge>
                {f.message} <span className="text-xs text-muted-foreground">ticket {f.record_id}</span>
              </li>
            ))}
          </ul>
        </CardContent>
      )}
    </Card>
  );
}
